import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgSelectComponent } from '@ng-select/ng-select';
import { User } from '@sentry/browser';
import { ProviderDataReviewListComponent } from 'app/capture-admin/components/provider-data-review-list/provider-data-review-list.component';
import { ProviderDataReviewType } from 'app/core/enums/provider-data-review-type.enum';
import { UserRoleName } from 'app/core/enums/user-role-name.enum';
import { UserSettings } from 'app/core/enums/user-settings.enum';
import { LimitOffsetPaging, Sort } from 'app/core/models/paged-results/limit-offset-paging.model';
import { ProviderDataReview } from 'app/core/models/provider-data-review.model';
import { ProviderDataReviewFilters, ProviderDataReviewQueueSettings } from 'app/core/models/user-settings/provider-data-review-settings.model';
import { keyedProviderDataReviewTypeOptions } from 'app/core/options/provider-data-review-type.opts';
import { NavigationService } from 'app/core/services/navigation.service';
import { PagedProviderDataReviews, ProviderDataReviewsService } from 'app/core/services/provider-data-reviews.service';
import { UserSettingsService } from 'app/core/services/user-settings.service';
import { UsersService } from 'app/core/services/users.service';
import { PaginationHeaderComponent } from 'app/shared/components/pagination-header/pagination-header.component';
import { PaginationComponent } from 'app/shared/components/pagination/pagination.component';
import { remove, isEmpty } from 'lodash-es';

enum ProviderDataReviewsSortBy {
  priorityDesc = 'priority_desc',
  estimatedValueDesc = 'estimated_value_desc',
  createdAtDesc = 'created_at_desc',
  nearestExpirationDateAsc = 'nearest_expiration_date_asc',
}

@Component({
    selector: 'app-capture-admin-provider-data-review-queue',
    styleUrls: ['./capture-admin-provider-data-review-queue.component.scss'],
    templateUrl: './capture-admin-provider-data-review-queue.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        NgSelectComponent,
        PaginationHeaderComponent,
        PaginationComponent,
        MatProgressSpinner,
        ProviderDataReviewListComponent,
    ]
})
export class CaptureAdminProviderDataReviewQueueComponent implements OnInit {
  private providerDataReviewsService = inject(ProviderDataReviewsService);
  private userSettingsService = inject(UserSettingsService);
  private navigationService = inject(NavigationService);
  private usersService = inject(UsersService);
  private formBuilder = inject(FormBuilder);

  dataReviews: ProviderDataReview[] = [];
  selectedDataReviewIds: number[] = [];

  filters: ProviderDataReviewFilters;
  sort: Sort;
  loading = false;
  paging = LimitOffsetPaging.empty;

  formGroup: FormGroup;

  assignedToUsers: User[] = [];

  keyedProviderDataReviewTypeOptions = keyedProviderDataReviewTypeOptions;

  typeOptions = [
    keyedProviderDataReviewTypeOptions[ProviderDataReviewType.providerSpecialtyDataReview],
    keyedProviderDataReviewTypeOptions[ProviderDataReviewType.providerFaxNumberDataReview],
  ];

  sortByOptions = [
    { value: ProviderDataReviewsSortBy.priorityDesc, display: 'Highest Priority' },
    { value: ProviderDataReviewsSortBy.createdAtDesc, display: 'Created On' },
    { value: ProviderDataReviewsSortBy.estimatedValueDesc, display: 'Estimated Value' },
    { value: ProviderDataReviewsSortBy.nearestExpirationDateAsc, display: 'Nearest Expiration' },
  ];

  static sortByValueToSort = {
    [ProviderDataReviewsSortBy.priorityDesc]: new Sort('priority', 'desc'),
    [ProviderDataReviewsSortBy.estimatedValueDesc]: new Sort('estimated_value', 'desc'),
    [ProviderDataReviewsSortBy.createdAtDesc]: new Sort('created_at', 'desc'),
    [ProviderDataReviewsSortBy.nearestExpirationDateAsc]: new Sort('nearest_expiration_date', 'asc'),
  };

  ngOnInit() {
    this.loadQueueSettings();
    this.loadReviews();
    this.loadAssignedToUsers();
    this.initFormGroup();
  }

  onPageSizeChange(size: number) {
    this.paging.pageSize = size;
    this.loadReviews();
    this.saveQueueSettings();
  }

  onPageChange() {
    this.loadReviews();
    this.saveQueueSettings();
  }

  onResetFiltersClick() {
    this.paging.currentPage = 1;

    this.sort =
      CaptureAdminProviderDataReviewQueueComponent.sortByValueToSort[this.sortByOptions[0].value];

    this.filters = new ProviderDataReviewFilters();

    this.loadReviews();
    this.saveQueueSettings();
    this.initFormGroup();
  }

  onProviderDataReviewSelected(providerDataReview: ProviderDataReview) {
    this.selectedDataReviewIds.push(providerDataReview.id);
  }

  onProviderDataReviewDeselected(providerDataReview: ProviderDataReview) {
    remove(this.selectedDataReviewIds, id => id === providerDataReview.id);
  }

  onAssignmentUpdate(userId) {
    this.providerDataReviewsService.batchAssignedTo(this.selectedDataReviewIds, userId).subscribe(() => {
      this.navigationService.reloadCurrentUrl();
    });
  }

  onAssignmentClearSelection() {
    this.selectedDataReviewIds = [];
  }

  get showPaginationHeader() {
    return isEmpty(this.selectedDataReviewIds);
  }

  private loadQueueSettings() {
    const queueSettings: ProviderDataReviewQueueSettings =
      this.userSettingsService.get<ProviderDataReviewQueueSettings>(
        UserSettings.captureAdminProviderDataReviewQueueSettings
      );

    if (queueSettings) {
      this.filters = queueSettings.filters;
      this.paging = queueSettings.paging;
      this.sort = queueSettings.sort;
    } else {
      this.filters = new ProviderDataReviewFilters();
      this.sort =
        CaptureAdminProviderDataReviewQueueComponent.
          sortByValueToSort[ProviderDataReviewsSortBy.priorityDesc];
    }
  }

  private saveQueueSettings() {
    const queueSettings = new ProviderDataReviewQueueSettings();
    queueSettings.filters = this.filters;
    queueSettings.paging = this.paging;
    queueSettings.sort =
      CaptureAdminProviderDataReviewQueueComponent.sortByValueToSort[this.formGroup.value.sort];

    this.userSettingsService.
      save<ProviderDataReviewQueueSettings>(
        UserSettings.captureAdminProviderDataReviewQueueSettings,
        queueSettings
      );
  }

  private initFormGroup() {
    this.formGroup = this.formBuilder.group({
      sort: `${this.sort.col}_${this.sort.dir}`,
      filters: this.formBuilder.group({
        reviewType: [this.filters.reviewType],
        assignedToIds: [this.filters.assignedToIds],
      })
    });

    this.formGroup.valueChanges.subscribe(_value => {
      this.paging.currentPage = 1;

      const formValue = this.formGroup.value;

      this.sort =
        CaptureAdminProviderDataReviewQueueComponent.sortByValueToSort[formValue.sort];

      this.filters = new ProviderDataReviewFilters();
      this.filters.reviewType = formValue.filters.reviewType;
      this.filters.assignedToIds = formValue.filters.assignedToIds;

      this.saveQueueSettings();
      this.loadReviews();
    })
  }

  private loadReviews(showLoading = true) {
    const paging = {
      page: this.paging.currentPage,
      pageSize: this.paging.pageSize
    };

    if (showLoading) {
      this.loading = true;
    }

    this.providerDataReviewsService.getList(paging, this.filters, this.sort).
      subscribe((result: PagedProviderDataReviews) => {
        this.dataReviews = result.records || [];
        this.paging = result.meta.paging;
        this.loading = false;
      });
  }

  private loadAssignedToUsers() {
    this.usersService.getByUserRole([UserRoleName.manageProviderData]).subscribe((result) => {
      this.assignedToUsers = [{ id: -1, fullName: 'UNASSIGN' } as User].concat(result.users);
    });
  }
}